import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { CategoryService } from './category.service';
import { BlobService } from '../../core/blob.service';
import { HttpEventType } from '@angular/common/http';

@Component({
  standalone: false,
  selector: 'app-category-form',
  template: `
    <div>
      <h2 class="text-2xl font-serif">{{ isEdit ? 'Editar' : 'Nova' }} Categoria</h2>
      <form [formGroup]="form" (ngSubmit)="onSubmit()" class="mt-4">
        <div class="mb-4">
          <label class="block">Nome</label>
          <input formControlName="nome" class="border p-2 w-full" />
        </div>
        <div class="mb-4">
          <label class="block">Slug (opcional)</label>
          <input formControlName="slug" class="border p-2 w-full" />
        </div>
        <div class="mb-4">
          <label class="block">Descrição</label>
          <textarea formControlName="descricao" rows="3" class="border p-2 w-full" placeholder="Descrição da categoria (opcional)"></textarea>
        </div>
        <div class="mb-4">
          <label class="block">Ordem de Exibição</label>
          <input formControlName="order" type="number" min="0" class="border p-2 w-32" placeholder="0" />
          <p class="text-xs text-gray-500 mt-1">Menor número = exibido primeiro</p>
        </div>

        <div class="mb-4">
          <label class="block">Imagem da categoria (Mandatorio)</label>
          <div class="flex items-center gap-2">
            <input type="file" (change)="onFile($event)" accept="image/*" [disabled]="uploading" />
          </div>
          <div *ngIf="previewUrl" class="mt-2">
            <img [src]="previewUrl" class="h-24 object-cover rounded" />
          </div>
          <!-- tooltip removed per request -->
          <div *ngIf="uploading" class="mt-2 w-full">
            <div class="w-full bg-gray-200 h-2 rounded overflow-hidden">
              <div [style.width.%]="uploadProgress" class="bg-green-500 h-2"></div>
            </div>
            <div class="text-sm text-gray-600 mt-1">Progresso: {{ uploadProgress }}%</div>
          </div>
          <!-- upload messages removed; UI shows only preview and validation errors -->
        </div>
        <div class="flex gap-2">
          <button type="submit" class="bg-primary text-white px-4 py-2 rounded" [disabled]="form.invalid || uploading">Salvar</button>
          <button type="button" class="px-4 py-2" (click)="cancel()">Cancelar</button>
        </div>
      </form>
    </div>
  `
})
export class CategoryFormComponent implements OnInit {
  form: FormGroup;
  isEdit = false;
  id?: string;
  createdId?: string | null = null;
  selectedFile?: File | null = null;
  previewUrl?: string | null = null;
  uploading = false;
  uploadProgress = 0;
  constructor(
    private fb: FormBuilder,
    private svc: CategoryService,
    private router: Router,
    private route: ActivatedRoute,
    private blob: BlobService
  ) {
    this.form = this.fb.group({ nome: ['', Validators.required], slug: [''], descricao: [''], order: [0], imageId: [null], imageBase64: [null, Validators.required], imageFilename: [null], imageContentType: [null] });
  }

  ngOnInit(): void {
    this.id = this.route.snapshot.params['id'];
    this.isEdit = !!this.id;
    if (this.isEdit && this.id) this.createdId = this.id;
    if (this.isEdit && this.id) {
      this.svc.get(this.id).subscribe((res: any) => {
        this.form.patchValue({ nome: res.nome, slug: res.slug, descricao: res.descricao ?? '', order: res.order ?? 0, imageId: res.imageId ?? null });
        const url = res.imageUrl || res.image?.url || res.imagem?.url || res.url || null;
        if (url) this.previewUrl = url;
      });
    }
  }

  onFile(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) {
      this.selectedFile = null;
      this.previewUrl = null;
      return;
    }
    this.selectedFile = input.files[0];
    const reader = new FileReader();
    reader.onload = (e: any) => this.previewUrl = e.target.result;
    reader.readAsDataURL(this.selectedFile);
    // also read base64 and set form control so the image becomes required
    this.readFileAsBase64(this.selectedFile).then((b64) => {
      this.form.controls['imageBase64'].setValue(b64);
      this.form.controls['imageFilename'].setValue(this.selectedFile?.name ?? null);
      this.form.controls['imageContentType'].setValue(this.selectedFile?.type ?? null);
    }).catch((err) => {
      console.error('[CategoryForm] failed to read file', err);
    });
  }

  private readFileAsBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        // strip data:*/*;base64, prefix if present
        const idx = result.indexOf('base64,');
        const base64 = idx >= 0 ? result.substring(idx + 7) : result;
        resolve(base64);
      };
      reader.onerror = (err) => reject(err);
      reader.readAsDataURL(file);
    });
  }

  uploadFile() {
    // upload button removed: upload is handled via create/update payload (embedded image on save)
    return;
  }

  private uploadWithCategory(categoryId?: string, navigateAfter = false) {
    if (!this.selectedFile) return;
    if (!categoryId) {
      // CategoryId absent: nothing to do for multipart fallback
      return;
    }
    const fd = new FormData();
    fd.append('file', this.selectedFile);
    fd.append('filename', this.selectedFile.name);
    fd.append('contentType', this.selectedFile.type || 'image');
    if (categoryId) fd.append('categoriaId', categoryId);

    this.uploading = true;
    this.uploadProgress = 0;

    const { events$ } = this.blob.uploadWithCancel('/api/v1/media/upload', fd, { retries: 2, backoffMs: 800 });
    events$.subscribe({
      next: (event: any) => {
        if (event.type === HttpEventType.UploadProgress) {
          this.uploadProgress = Math.round((100 * event.loaded) / (event.total || event.loaded));
        } else if (event.type === HttpEventType.Response) {
          const body = event.body || {};
          const id = body.imageId || body.id || body.Id || body.mediaId || body.media_id;
          const url = body.imageUrl || body.url || body.Url || body.path;
          if (id) this.form.controls['imageId'].setValue(id);
          if (url) this.previewUrl = url;
          this.selectedFile = null;
          this.uploading = false;
          this.uploadProgress = 100;
          // persist createdId if not present
          if (!this.createdId && categoryId) this.createdId = categoryId;

          // After successful upload, ensure the category is associated with this media.
          // Prefer updating by imageId (backend expects imageId/mediaId). Then refresh category to obtain imageUrl.
          if (id && categoryId) {
            this.svc.update(categoryId, { imageId: id }).subscribe({
              next: () => {
                this.svc.get(categoryId).subscribe((cat: any) => {
                  this.form.patchValue({ imageId: cat.imageId ?? null });
                  const refreshedUrl = cat.imageUrl || cat.image?.url || null;
                  if (refreshedUrl) this.previewUrl = refreshedUrl;
                  if (navigateAfter) this.router.navigate(['/admin/categories']);
                });
              },
              error: (err: any) => {
                console.error('[CategoryForm] failed to associate media with category', err);
                if (navigateAfter) this.router.navigate(['/admin/categories']);
              }
            });
          } else if (navigateAfter && categoryId) {
            // fallback: refresh category to pick up any server-side association
            this.svc.get(categoryId).subscribe((cat: any) => {
              this.form.patchValue({ imageId: cat.imageId ?? null });
              if (cat.imageUrl) this.previewUrl = cat.imageUrl;
              this.router.navigate(['/admin/categories']);
            });
          }
        }
      },
      error: (err: any) => {
        console.error('[CategoryForm] upload failed', err);
        this.uploading = false;
      }
    });
  }

  onSubmit() {
    if (this.isEdit && this.id) {
      // update: if file selected, embed image content in payload so backend will upload and associate
      const doUpdate = (payload: any) => {
        this.svc.update(this.id as string, payload).subscribe({
          next: () => this.router.navigate(['/admin/categories']),
          error: (err: any) => this.handleServerError(err)
        });
      };

      const payloadBase = this.form.value;
      if (this.selectedFile) {
        this.readFileAsBase64(this.selectedFile).then((b64) => {
          payloadBase.imageBase64 = b64;
          payloadBase.imageFilename = this.selectedFile?.name ?? null;
          payloadBase.imageContentType = this.selectedFile?.type ?? null;
          doUpdate(payloadBase);
        }).catch((err) => this.handleServerError(err));
      } else {
        doUpdate(payloadBase);
      }
      return;
    }
    // create: if file selected, read it and include content in payload so backend uploads automatically
    const payload = this.form.value;
    if (this.selectedFile) {
      this.readFileAsBase64(this.selectedFile).then((b64) => {
        payload.imageBase64 = b64;
        payload.imageFilename = this.selectedFile?.name ?? null;
        payload.imageContentType = this.selectedFile?.type ?? null;
        this.svc.create(payload).subscribe({
          next: (res: any) => {
            const createdId = res?.id || res?.Id || res?.categoriaId;
            this.createdId = createdId ?? null;
            // refresh category to obtain imageUrl if backend associated
            if (createdId) {
              this.svc.get(createdId).subscribe(() => this.router.navigate(['/admin/categories']));
            } else {
              this.router.navigate(['/admin/categories']);
            }
          },
          error: (err: any) => this.handleServerError(err)
        });
      }).catch((err) => this.handleServerError(err));
    } else {
      this.svc.create(payload).subscribe({
        next: (res: any) => {
          const createdId = res?.id || res?.Id || res?.categoriaId;
          this.createdId = createdId ?? null;
          this.router.navigate(['/admin/categories']);
        },
        error: (err: any) => this.handleServerError(err)
      });
    }
  }

  private handleServerError(err: any) {
    console.error('[CategoryForm] server error', err);
    // user-visible messages removed; log server error and map field errors to controls
    if (!err || !err.status) return;
    if (err.status === 400 && err.error && err.error.errors) {
      const errors = err.error.errors;
      Object.keys(errors).forEach((key) => {
        const messages = errors[key];
        const ctrl = this.form.get(key);
        const msg = Array.isArray(messages) ? messages.join('; ') : String(messages);
        if (ctrl) {
          ctrl.setErrors({ server: msg });
          ctrl.markAsTouched();
        } else {
          // attach to form-level errors if field not found
          this.form.setErrors({ server: msg });
        }
      });
    } else if (err.status >= 500) {
      // server error; keep console log
    } else if (err.error && err.error.message) {
      // backend message available; keep in console
    }
  }

  cancel() { this.router.navigate(['/admin/categories']); }
}
